<template>
  <el-dialog 
    :visible.sync="localVisible" 
    @close="handleClose"
    :title="fileName" 
    width="90%"
    top="5vh"
    class="file-preview-dialog"
    :class="{'has-scroll': contentRequireScroll}"
    :modal="false"
  >
    <!-- 压缩文件导航面包屑 -->
    <div v-if="archiveData" class="archive-nav">
      <el-breadcrumb separator="/" class="breadcrumb">
        <el-breadcrumb-item 
          v-for="(path, index) in currentPath" 
          :key="index"
          @click.native="handleBreadcrumb(index)"
          class="breadcrumb-item"
        >
          <i class="el-icon-folder"></i>
          {{ path || '根目录' }}
        </el-breadcrumb-item>
      </el-breadcrumb>
    </div>

     <!-- 普通文件预览 -->
    <div v-if="!isArchive" class="preview-content">
      <!-- 新增标题栏 -->
      <div class="preview-header">
        <span class="file-name">{{ fileName }}</span>
        <el-tag effect="dark" size="mini">{{ fileType.toUpperCase() }}</el-tag>
      </div>

      <div class="adaptive-container" :class="fileType">
        <!-- PDF预览 -->
        <pdf-viewer 
          v-if="fileType === 'pdf' && previewContent" 
          :src="previewContent" 
          class="pdf-preview"
        />
        
        <!-- 图片预览 -->
        <div v-else-if="isImage && previewContent" class="image-container">
          <img :src="previewContent" class="preview-image">
        </div>

        <!-- 文本预览 -->
        <pre 
          v-else-if="fileType === 'txt' && previewContent" 
          class="text-preview"
        >{{ previewContent }}</pre>

        <!-- Office文件 -->
        <iframe 
          v-else-if="isOffice" 
          :src="officeIframeSrc"
          class="office-iframe"
        />
      </div>


    </div>

    <!-- 压缩文件列表 -->
    <div v-if="archiveData" class="archive-list">
      <!-- 新增列表标题 -->
      <div class="list-header">
        <span>文件列表</span>
        <span class="file-count">{{ archiveTree.length }} 个项目</span>
      </div>
      
      <!-- 树形列表样式调整 -->
      <el-tree
        :data="archiveTree"
        node-key="fullPath"
        :props="treeProps"
        @node-click="handleFileClick"
      >
        <!-- 节点样式优化 -->
        <div slot-scope="{ node }" class="file-item">
          <i :class="fileIcon(node.data)" class="file-icon"></i>
          <span class="file-name" :class="{ 'unpreviewable': !node.data.previewable }">
            {{ node.label }}
            <span class="file-size" v-if="!node.data.fileObj.dir">
              ({{ formatSize(node.data.fileObj._data.uncompressedSize) }}) 
            </span>
          </span>
        </div>
      </el-tree>
    </div>


    <!-- 子预览对话框 -->
    <el-dialog 
      :visible.sync="innerVisible" 
      width="70%" 
      top="10vh"
      append-to-body
      class="inner-dialog"
    >
      <!-- 文本预览 -->
      <pre v-if="innerFileType === 'txt'">{{ innerContent }}</pre>
      
      <!-- PDF预览 -->
      <pdf-viewer v-else-if="innerFileType === 'pdf'" :src="innerUrl" />
      
      <!-- 图片预览 -->
      <img 
        v-else-if="isImageType" 
        :src="innerUrl" 
        class="preview-image"
        @load="handleImageLoad"
      >
      
      <!-- DOCX预览 -->
      <div 
        v-else-if="innerFileType === 'docx'" 
        ref="docxPreview" 
        class="docx-preview-container"
      ></div>
      
      <!-- Excel预览 -->
      <div 
        v-else-if="innerFileType === 'xlsx'" 
        class="excel-preview" 
        v-html="innerContent"
      ></div>
      
      <!-- 不支持的格式 -->
      <div v-else class="unsupported-file">
        暂不支持此文件类型预览，请下载后查看
      </div>
    </el-dialog>


  </el-dialog>
</template>

<script>
import axios from 'axios'
import JSZip from 'jszip'
import PdfViewer from './PdfViewer.vue'

import { renderAsync } from 'docx-preview'
import * as XLSX from 'xlsx'

const mimeTypeMap = {
  pdf: 'application/pdf',
  txt: 'text/plain',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
}

export default {
  name: 'FilePreview',
  components: { PdfViewer },
  props: {
    url: String,
    visible: {
      type: Boolean,
      default: false
    },
    fileName: { // 新增filename prop
      type: String,
      required: true
    }
  },
  data() {
    return {
      isArchive: false,
      previewContent: null,
      archiveData: null,
      currentPath: [],
      archiveTree: [],
      innerVisible: false,
      innerUrl: '',
      innerFileType:'',
      treeProps: {
        label: 'name',
        children: 'children'
      },
      // 新增数据项
      innerContent: '',
      imageVisible: false,
      localVisible: this.visible
    }
  },
  watch: {
    visible(val) {
      if (val) this.initPreview()
      this.localVisible = val
    },
    innerVisible(val) {
      if (!val) {
        // 释放Blob URL
        if (this.innerUrl && this.innerUrl.startsWith('blob:')) {
          URL.revokeObjectURL(this.innerUrl)
        }
        this.innerUrl = ''
        this.innerContent = ''
      }
    }
  },
  computed: {
    isImage() {
      return ['jpg', 'jpeg', 'png', 'gif'].includes(this.fileType)
    },
    isOffice() {
      return ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(this.fileType)
    },
    isImageType() {
      return ['jpg', 'jpeg', 'png', 'gif'].includes(this.innerFileType)
    },
    officeIframeSrc() {
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(this.url)}`
    },
    contentRequireScroll() {
      // 根据内容类型判断是否需要滚动
      return ['pdf', 'txt', 'xlsx'].includes(this.fileType)
    },
    fileType() {
      return this.getFileExt(this.fileName)
    },
    baseName() {
      return this.fileName.replace(/\.[^/.]+$/, "") // 移除扩展名
    }
  },
  methods: {
    async initPreview() {
      this.archiveData = null
      this.currentPath = []
      this.archiveTree = []
      const fileExt = this.getFileExt(this.fileName)
      this.isArchive = ['zip', 'rar'].includes(fileExt)

      if (this.isArchive) {
        await this.handleArchive()
      } else {
        await this.handleNormalFile()
      }
    },

    formatSize(bytes) {
      if (!bytes || isNaN(bytes)) return '0 B'
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    async handleNormalFile() {
      const response = await axios.get(this.url, { responseType: 'blob' })
      const blob = new Blob([response.data])
      
      if (this.fileType === 'pdf') {
        this.previewContent = URL.createObjectURL(blob)
      } else if (this.isImage) {
        this.previewContent = URL.createObjectURL(blob)
      } else if (this.fileType === 'txt') {
        this.previewContent = await blob.text()
      }
    },

    async handleArchive() {
      const response = await axios.get(this.url, { responseType: 'arraybuffer' })
      const zip = new JSZip()
      this.archiveData = await zip.loadAsync(response.data)
      this.currentPath = []
      this.buildArchiveTree()
    },

    // 重构后的目录树构建方法
    buildArchiveTree() {
      const treeData = []
      const currentFolder = this.getCurrentFolder()
      
      currentFolder.forEach((relativePath, file) => {
        if (!file.dir) { // 只处理文件
          const fileInfo = {
            name: relativePath.split('/').pop(),
            fullPath: [...this.currentPath, relativePath].join('/'),
            ext: this.getFileExt(relativePath),
            previewable: this.isPreviewable(this.getFileExt(relativePath)),
            fileObj: file  // 保存文件对象引用
          }
          treeData.push(fileInfo)
        }
      })
      
      this.archiveTree = treeData
    },


    // 获取当前路径对应的文件夹
    getCurrentFolder() {
      let folder = this.archiveData
      for (const dir of this.currentPath) {
        folder = folder.folder(dir)
      }
      return folder
    },

     // 获取完整路径
    getFullPath(name) {
      return [...this.currentPath, name].join('/')
    },


    getNodeByPath(path) {
      let currentNode = this.archiveData
      const pathParts = path.split('/').filter(p => p)
      
      for (const part of pathParts) {
        currentNode = currentNode.folder(part)
      }
      return currentNode
    },

    formatTreeData(node) {
      return Object.keys(node.files).map(filePath => {
        const file = node.files[filePath]
        return {
          path: filePath,
          name: file.name,
          isDir: file.dir,
          ext: this.getFileExt(file.name)
        }
      })
    },

    // 修改后的文件点击处理
    handleFileClick(file) {
        if (file.fileObj.dir) {
            this.currentPath.push(file.name)
            this.buildArchiveTree()
        } else if (file.previewable) {
            this.previewInnerFile(file)
        }
    },

    async previewInnerFile(file) {
      try {
        const targetFile = this.archiveData.file(file.fullPath)
        if (!targetFile) throw new Error('文件不存在')

        const arrayBuffer = await targetFile.async('arraybuffer')
        const ext = this.getFileExt(file.name).toLowerCase()
        const blob = new Blob([arrayBuffer], { type: mimeTypeMap[ext] })

        this.innerFileType = ext
        this.innerUrl = URL.createObjectURL(blob)

        // 特殊格式处理
        if (ext === 'txt') {
          this.innerContent = await blob.text()
        } 
        else if (ext === 'docx') {
          this.$nextTick(() => {
            renderAsync(blob, this.$refs.docxPreview)
          })
        }
        else if (ext === 'xlsx') {
          const data = new Uint8Array(arrayBuffer)
          const workbook = XLSX.read(data, { type: 'array' })
          const html = XLSX.utils.sheet_to_html(workbook.Sheets[workbook.SheetNames[0]])
          this.innerContent = html
        }

        this.innerVisible = true

      } catch (error) {
        console.error('预览失败:', error)
        this.$message.error(`预览失败：${error.message}`)
        if (this.innerUrl) URL.revokeObjectURL(this.innerUrl)
      }
    },

    handleImageLoad() {
      this.imageVisible = true
    },

    isPreviewable(ext) {
      const previewTypes = [
        'txt', 'pdf', 'doc', 'docx', 'xls', 'xlsx',
        'ppt', 'pptx', 'jpg', 'jpeg', 'png', 'gif'
      ]
      return previewTypes.includes(ext)
    },

    fileIcon(file) {
      return file.isDir ? 'el-icon-folder' : `el-icon-document-${file.ext}`
    },

    getFileExt(filename) {
      const parts = filename.split('.')
      if (parts.length === 1) return '' // 无扩展名情况
      return parts.pop().toLowerCase()
    },

    handleBreadcrumb(index) {
      this.currentPath = this.currentPath.slice(0, index + 1)
      this.buildArchiveTree(this.currentPath.join('/'))
    },
    handleClose() {
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style scoped>

.office-iframe {
  width: 100%;
  height: 70vh;
  border: none;
}


.archive-nav {
  margin-bottom: 15px;
}


.docx-preview-container {
  height: 70vh;
  overflow: auto;
  padding: 20px;
  background: #f8f8f8;
}

.excel-preview {
  height: 70vh;
  overflow: auto;
  padding: 20px;
}

.unsupported-file {
  padding: 20px;
  text-align: center;
  color: #666;
}


/* 新增整体对话框样式 */
.file-preview-dialog {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

::v-deep .el-dialog__header {
  border-bottom: 1px solid #ebeef5;
  padding: 12px 20px;
}

::v-deep .el-dialog__title {
  font-size: 16px;
  color: #303133;
}

/* 预览容器样式 */
.preview-content {
  height: 75vh;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #f8fafc;
}

.preview-header {
  padding: 12px;
  background: #fff;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.preview-container {
  height: calc(100% - 46px);
  padding: 20px;
  overflow: auto;
}

/* 压缩文件列表样式 */
.archive-list {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fff;
}

.list-header {
  padding: 12px;
  background: #f5f7fa;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  justify-content: space-between;
  font-weight: 500;
}

.file-item {
  padding: 8px 12px;
  transition: background 0.3s;
}

.file-item:hover {
  background: #f5f7fa;
}

.file-icon {
  margin-right: 8px;
  color: #909399;
}

.file-name {
  color: #606266;
}

.unpreviewable {
  opacity: 0.6;
}

/* 子对话框样式 */
.inner-dialog ::v-deep .el-dialog__header {
  background: #f5f7fa;
}

/* PDF容器优化 */
.pdf-viewer-container {
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 图片预览优化 */
.preview-image {
  border-radius: 4px;
  background: 
    repeating-linear-gradient(45deg, #f8f8f8 25%, transparent 25%, transparent 75%, #f8f8f8 75%),
    repeating-linear-gradient(45deg, #f8f8f8 25%, #fff 25%, #fff 75%, #f8f8f8 75%);
  background-position: 0 0, 10px 10px;
  background-size: 20px 20px;
  padding: 10px;
}

/* 加载状态 */
.loading-wrapper {
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}


/* 新增自适应容器 */
.adaptive-container {
  height: calc(70vh - 46px); /* 扣除标题栏高度 */
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  position: relative;
}

/* PDF预览优化 */
.pdf-preview {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  
  ::v-deep .pdf-viewer-container {
    margin: 0 auto;
    min-width: min-content;
  }
  
  ::v-deep .pdf-page {
    margin: 20px auto;
  }
}

/* 图片容器 */
.image-container {
  max-width: 100%;
  max-height: 100%;
  overflow: auto;
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  margin: auto;
  display: block;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

/* 文本预览 */
.text-preview {
  width: 100%;
  height: 100%;
  padding: 20px;
  overflow: auto;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
}

/* Office文件 */
.office-iframe {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 4px;
  background: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

/* 滚动条样式优化 */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .adaptive-container {
    height: calc(80vh - 46px);
  }
  
  .text-preview {
    font-size: 14px;
    padding: 12px;
  }
}

</style>